import { Color } from '@persona-id/design-tokens';
import type { Action, Location } from 'history';
import { isEqual } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import NavBack from 'dashboard/components/NavBack';
import { OptionType } from 'dashboard/components/ReactSelect';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import {
  AddDataSourceResponse,
  FormNewDataSourceData,
  FormNewDataSourceDataS3,
} from 'dashboard/forms/FormNewDataSource';
import Form from 'dashboard/layouts/FormLayout/Form';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { AccountType } from 'dashboard/models/account-type';
import {
  ExternalIntegrationDataSource,
  ExternalIntegrationStatus,
  GoogleSheetsFilesResponse,
  S3Config,
  S3Object,
} from 'dashboard/models/external-integration';
import {
  Importer,
  IMPORTER_TYPES,
  ImporterArchiveAfterCompletion,
  ImporterArraySyncMode,
  ImporterSyncMode,
  ImporterType,
  RECURRABLE_IMPORTERS,
} from 'dashboard/models/importer';
import { TransactionType } from 'dashboard/models/transaction-type';
import ConfigureTableStep from 'dashboard/views/DashboardImporterStepperView/ConfigureTableStep';
import SelectDataSourceStep from 'dashboard/views/DashboardImporterStepperView/SelectDataSourceStep';
import SetDestinationStep from 'dashboard/views/DashboardImporterStepperView/SetDestinationStep';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';
import isEmpty from 'lib/utils/formValidation/isEmpty';

enum StepKey {
  SelectDataSource = 'select-data-source',
  ConfigureTable = 'configure-table',
  SetDestination = 'set-destination',
}

const ORDERED_STEPS = [StepKey.SelectDataSource, StepKey.ConfigureTable, StepKey.SetDestination];

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
`;

const Breadcrumb = styled.span`
  color: ${Color.CoreSlate700};
`;

export interface NewImporterFormData {
  dataSourceId: string;
  destination: 'Account' | 'Txn';
  destinationTypeId: string;
  name: string;
  type: ImporterType;
  'table-name': string;
  syncMode: ImporterSyncMode;
  arraySyncMode: ImporterArraySyncMode;
  archiveAfterCompletion: ImporterArchiveAfterCompletion;
}

export class StepManager {
  private readonly importerType: ImporterType;
  private readonly importerName: string;
  private readonly selectedImporterType: string;
  private readonly dataSourceId: string;
  private readonly dataSources: ExternalIntegrationDataSource[];
  private readonly tableName: string | null;
  private readonly syncMode: ImporterSyncMode;
  private readonly arraySyncMode: ImporterArraySyncMode;
  private readonly archivedAfterCompletion: string;
  private readonly destination: string;
  private readonly destinationTypeId: string;

  public constructor(
    importerType: ImporterType,
    importerName: string,
    selectedImporterType: string,
    dataSourceId: string,
    dataSources: ExternalIntegrationDataSource[],
    destination: string,
    destinationTypeId: string,
    tableName: string | null,
    syncMode: ImporterSyncMode,
    arraySyncMode: ImporterArraySyncMode,
    archivedAfterCompletion: string,
  ) {
    this.importerType = importerType;
    this.importerName = importerName;
    this.selectedImporterType = selectedImporterType;
    this.dataSourceId = dataSourceId;
    this.dataSources = dataSources;
    this.destination = destination;
    this.destinationTypeId = destinationTypeId;
    this.tableName = tableName;
    this.syncMode = syncMode;
    this.arraySyncMode = arraySyncMode;
    this.archivedAfterCompletion = archivedAfterCompletion;
  }

  public canAdvanceFrom(stepKey: StepKey): boolean {
    switch (stepKey) {
      case StepKey.SelectDataSource:
        if (isEmpty(this.importerName) || this.importerName.trim().length < 3) {
          return false;
        }
        return !isEmpty(this.selectedImporterType);
      case StepKey.ConfigureTable: {
        if (isEmpty(this.dataSourceId)) {
          return false;
        }
        const dataSource = this.dataSources.find((d) => d.id === this.dataSourceId);

        if (!dataSource || dataSource.attributes.status !== ExternalIntegrationStatus.Active) {
          return false;
        }
        // gsheets importers also need a table name specified
        if (
          this.importerType === ApiModel.ImporterPipelineGoogleSheets &&
          (this.tableName == null || this.tableName === '')
        ) {
          return false;
        }

        return this.canAdvanceFrom(StepKey.SelectDataSource);
      }
      case StepKey.SetDestination:
        return (
          !isEmpty(this.destination) &&
          !isEmpty(this.destinationTypeId) &&
          !isEmpty(this.syncMode) &&
          !isEmpty(this.arraySyncMode) &&
          !isEmpty(this.archivedAfterCompletion) &&
          this.canAdvanceFrom(StepKey.ConfigureTable)
        );
      default:
        throw new Error(`Invalid StepKey: ${stepKey}`);
    }
  }

  public canOpen(stepKey: StepKey): boolean {
    return this.stepIdx(stepKey) <= this.farthestNavigableStepIdx();
  }

  public getStepFromPath(url: string) {
    if (url.match('/select-data-source$')) {
      return StepKey.SelectDataSource;
    }
    if (url.match('/configure-table$')) {
      return StepKey.ConfigureTable;
    }
    if (url.match('/set-destination$')) {
      return StepKey.SetDestination;
    }
    // clear form data from local storage if there was no step path match
    StorageClient.removeLocalItem(LocalStorageKey.DashboardImporterStepperFormInputs);
    return undefined;
  }

  public inferStep(url: string) {
    const stepFromPath = this.getStepFromPath(url) || StepKey.SelectDataSource;
    return ORDERED_STEPS[Math.min(this.stepIdx(stepFromPath), this.farthestNavigableStepIdx())];
  }

  private farthestNavigableStepIdx() {
    let lastValidStepIdx = ORDERED_STEPS.length - 1;
    for (; lastValidStepIdx > 0; --lastValidStepIdx) {
      if (this.canAdvanceFrom(ORDERED_STEPS[lastValidStepIdx - 1])) {
        break;
      }
    }
    return lastValidStepIdx;
  }

  private stepIdx(stepKey: StepKey) {
    return Math.max(0, ORDERED_STEPS.indexOf(stepKey));
  }
}

interface DashboardImporterStepperViewProps {
  accountTypes: AccountType[];
  addImporter: (values: NewImporterFormData) => Promise<void>;
  dataSources: ExternalIntegrationDataSource[];
  hasTransactionsEnabled: boolean;
  currentImporterId?: string | null;
  importers: Importer[];
  txnTypes: TransactionType[];
  updateImporter: (values: NewImporterFormData) => Promise<void>;
  addDataSource: (values: FormNewDataSourceData) => Promise<AddDataSourceResponse>;
  refreshDataSource: (dataSource: ExternalIntegrationDataSource) => Promise<void>;
  s3Config: S3Config | null;
  getAllGoogleSheets: (code: string, redirectUrl: string) => Promise<GoogleSheetsFilesResponse>;
  handleListS3Objects: (values: FormNewDataSourceDataS3) => Promise<S3Object[]>;
}

const DashboardImporterStepperView = ({
  accountTypes,
  addImporter,
  dataSources,
  hasTransactionsEnabled,
  currentImporterId,
  importers,
  txnTypes,
  updateImporter,
  addDataSource,
  refreshDataSource,
  s3Config,
  getAllGoogleSheets,
  handleListS3Objects,
}: DashboardImporterStepperViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const importer = importers.find((importer) => importer.id === currentImporterId) || null;
  const importerExists = !!importer;

  const [cachedFormData, saveFormData] = useLocalStorage<Partial<NewImporterFormData>>(
    LocalStorageKey.DashboardImporterStepperFormInputs,
    {
      dataSourceId: '',
      destinationTypeId: '',
      name: '',
      'table-name': '',
    },
  );

  const {
    control,
    register,
    formState: { errors, isValid, isSubmitting },
    resetField,
    handleSubmit,
    setValue,
    watch,
  } = useForm<NewImporterFormData>({
    mode: 'onChange',
    defaultValues: {
      type: importer?.type ?? (cachedFormData.type as ImporterType) ?? ('' as ImporterType), // Ensure '' is a valid ImporterType
      name: importer?.attributes.name ?? cachedFormData.name ?? '',
      dataSourceId:
        importer?.relationships['external-integration'].data.id ??
        cachedFormData.dataSourceId ??
        '',
      destinationTypeId:
        importer?.attributes['pipeline-config']['destination-type-id'] ??
        cachedFormData.destinationTypeId,
      destination:
        importer?.attributes['pipeline-config'].destination ??
        (cachedFormData.destination as 'Account' | 'Txn') ??
        'Account',
      'table-name': '',
      syncMode: ImporterSyncMode.Upsert,
      arraySyncMode: ImporterArraySyncMode.Append,
      archiveAfterCompletion: ImporterArchiveAfterCompletion.True,
    },
  });

  // watch for changes in the form
  const selectedImporterType = watch('type');
  const importerName = watch('name');
  const dataSourceId = watch('dataSourceId');
  const destinationTypeId = watch('destinationTypeId');
  const destination = watch('destination');
  const tableName = watch('table-name');
  const syncMode = watch('syncMode');
  const arraySyncMode = watch('arraySyncMode');
  const archiveAfterCompletion = watch('archiveAfterCompletion');

  const isActiveStep = (step: StepKey) => {
    return step === currentStep;
  };

  const stepManager = useMemo(
    () =>
      new StepManager(
        selectedImporterType,
        importerName,
        selectedImporterType,
        dataSourceId,
        dataSources,
        destination,
        destinationTypeId,
        tableName,
        syncMode,
        arraySyncMode,
        archiveAfterCompletion,
      ),
    [
      selectedImporterType,
      importerName,
      selectedImporterType,
      dataSourceId,
      JSON.stringify(dataSources),
      destination,
      destinationTypeId,
      tableName,
      syncMode,
      arraySyncMode,
      archiveAfterCompletion,
    ],
  );

  const onSetStep = (step: StepKey) => {
    if (!stepManager.canOpen(step)) {
      return;
    }
    setCurrentStep(step);
  };

  const [currentStep, setCurrentStep] = useState(stepManager.inferStep(location.pathname));
  const navigateToStep = useCallback(
    (step: StepKey) => {
      const currentUrl = location.pathname;
      const stepFromPathRegexp = new RegExp(`/${stepManager.getStepFromPath(currentUrl)}$`);
      if (currentUrl.match(stepFromPathRegexp)) {
        const newUrl = currentUrl.replace(stepFromPathRegexp, '/' + step);
        navigate(newUrl);
      } else {
        navigate(currentUrl + '/' + step);
      }
    },
    [navigate, location.pathname, stepManager],
  );

  useEffect(() => {
    navigateToStep(currentStep);
  }, [currentStep, navigateToStep]);

  // filter out importer types based on feature flags
  const includeRedshiftOption = useFeatureFlag('dashboard:graph-redshift-importer');
  const includeGoogleSheetsOption = useFeatureFlag('dashboard:graph-google-sheets-importer');
  const filterDisabledDataSource = (key: any) => {
    const isRedshift = key === ApiModel.ImporterPipelineRedshift;
    const isGoogleSheetsOption = key === ApiModel.ImporterPipelineGoogleSheets;
    // remove importer from options if flag is disabled
    return (
      (includeRedshiftOption || !isRedshift) && (includeGoogleSheetsOption || !isGoogleSheetsOption)
    );
  };

  const importerTypes = Object.keys(IMPORTER_TYPES)
    .filter(filterDisabledDataSource)
    .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
    .map((key) => ({
      label: IMPORTER_TYPES[key as keyof typeof IMPORTER_TYPES],
      value: key as ImporterType,
    }));

  useEffect(() => {
    saveFormData({
      type: selectedImporterType,
      name: importerName,
      dataSourceId,
      destinationTypeId,
      destination,
      syncMode,
      arraySyncMode,
      archiveAfterCompletion,
    });
    return () => {
      // clear form data from local storage when component unmounts
      StorageClient.removeLocalItem(LocalStorageKey.DashboardImporterStepperFormInputs);
    };
  }, [
    importer,
    importerName,
    selectedImporterType,
    watch,
    dataSourceId,
    destinationTypeId,
    location.pathname,
    syncMode,
    arraySyncMode,
    archiveAfterCompletion,
  ]);

  const onSubmit = async (data: NewImporterFormData) => {
    if (!isValid || isSubmitting) return;

    await (importerExists ? updateImporter : addImporter)(data);
  };

  const destinationOptions: OptionType<'Account' | 'Txn'>[] = [
    { label: 'Account', value: 'Account' },
    {
      label: 'Transaction',
      value: 'Txn',
      disabled: !hasTransactionsEnabled || txnTypes.length === 0,
    },
  ];

  let destinationTypes: (AccountType | TransactionType)[] = [];
  destinationTypes =
    destination === 'Account' ? accountTypes : destination === 'Txn' ? txnTypes : [];

  const destinationType = destinationTypes.find((d) => d.id === destinationTypeId);
  useEffect(() => {
    if (!destinationType) {
      setValue('destinationTypeId', destinationTypes[0]?.id);
    }
  }, [destinationType]);
  const destinationTypeIdOptions = destinationTypes.map((d) => {
    return { label: d.attributes['name'], value: d.id };
  });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <NavConfirmPrompt
        prevent={(currentLocation: Location, nextLocation?: Location, _action?: Action) => {
          const currentBasePath = currentLocation.pathname.split('/').slice(0, -1);
          const nextBasePath = nextLocation?.pathname?.split('/')?.slice(0, -1);
          if (isEqual(currentBasePath, nextBasePath)) {
            return false;
          }
          const importerIncomplete = true;
          return !importerIncomplete;
        }}
        message='Are you sure you want to leave?'
      >
        The changes you made wont be saved.
      </NavConfirmPrompt>
      <Header
        title=''
        prepend={
          <HeaderTitle>
            <NavBack to='/dashboard/imports/importers'>
              <Breadcrumb>Imports</Breadcrumb>
            </NavBack>
          </HeaderTitle>
        }
      ></Header>
      <Container>
        <Section>
          <SelectDataSourceStep
            control={control}
            canAdvance={stepManager.canAdvanceFrom(StepKey.SelectDataSource)}
            errors={errors}
            handleNextButtonClick={() => onSetStep(StepKey.ConfigureTable)}
            handleSelectImporterType={(type: string) => {
              setValue('type', type as keyof typeof IMPORTER_TYPES);
              resetField('dataSourceId');
              if (RECURRABLE_IMPORTERS.includes(type as keyof typeof IMPORTER_TYPES)) {
                setValue('archiveAfterCompletion', ImporterArchiveAfterCompletion.False);
              } else {
                setValue('archiveAfterCompletion', ImporterArchiveAfterCompletion.True);
              }
            }}
            importerTypes={importerTypes}
            isActive={isActiveStep(StepKey.SelectDataSource)}
            handleCardClick={() => onSetStep(StepKey.SelectDataSource)}
            onImporterNameChange={(name: string) => setValue('name', name)}
            selectedImporterType={selectedImporterType}
          />
          <ConfigureTableStep
            control={control}
            register={register}
            canAdvance={stepManager.canAdvanceFrom(StepKey.ConfigureTable)}
            dataSources={dataSources}
            datasourceId={dataSourceId}
            handleNextButtonClick={() => onSetStep(StepKey.SetDestination)}
            importerType={selectedImporterType}
            isActive={isActiveStep(StepKey.ConfigureTable)}
            onSelectedDataSourceChange={(id: string) => setValue('dataSourceId', id)}
            handleCardClick={() => onSetStep(StepKey.ConfigureTable)}
            addDataSource={addDataSource}
            refreshDataSource={refreshDataSource}
            getAllGoogleSheets={getAllGoogleSheets}
            s3Config={s3Config}
            handleListS3Objects={handleListS3Objects}
          />
          <SetDestinationStep
            control={control}
            register={register}
            canAdvance={stepManager.canAdvanceFrom(StepKey.SetDestination)}
            destination={destination}
            destinationOptions={destinationOptions}
            destinationTypeId={destinationTypeId}
            destinationTypeIdOptions={destinationTypeIdOptions}
            syncMode={syncMode}
            arraySyncMode={arraySyncMode}
            isActive={isActiveStep(StepKey.SetDestination)}
            isSubmitting={isSubmitting}
            isValid={isValid}
            handleCardClick={() => onSetStep(StepKey.SetDestination)}
          />
        </Section>
      </Container>
    </Form>
  );
};

export default DashboardImporterStepperView;
